<template>
    <div id="agent-list">
        <supsis-table
            :columns="getCols()"
            :rows="items()"
            :bottom-pagination="true"
            :pagination-cursor="paginationCursor"
            @cursor-changed="(cursor) => (paginationCursor = cursor)"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCheckCircle, faClock, faGlobeEurope, faMinusCircle, faPowerOff } from "@fortawesome/free-solid-svg-icons";
import PassedTimeCounterTimeFormat from "./PassedTimeCounterTimeFormat";
import * as _ from "lodash";
import { log2 } from "../utils/log2";
import moment from "moment";
import SYSTEM_PUBSUB, { systemMessageStream } from "../utils/systemPubSub";
import { filter } from "rxjs/operators";
import { sendSocketMessage } from "../utils/socketStream";
import Pagination from "./Pagination";
import dmIcon from "./svg/DmIcon.vue";
import SupsisTable from "./SupsisTable.vue";

library.add(faCheckCircle, faClock, faMinusCircle, faPowerOff, faGlobeEurope);
const log = log2("AgentList");

export default {
    name: "AgentList",
    components: {
        SupsisTable,
        PassedTimeCounterTimeFormat,
        Pagination,
        dmIcon,
    },
    props: {
        assign: {
            type: Boolean,
            default: false,
        },
        forward: {
            type: Boolean,
            default: false,
        },
        infoButtonVisible: {
            type: Boolean,
            default: false,
        },
        reportButton: {
            type: Boolean,
            default: false,
        },
        showOnlineAgents: {
            type: Boolean,
            default: false,
        },
        defaultContacts: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            isBusy: true,
            date1: "  2019-12-06T12:50:27.453Z",
            date2: "  2019-12-06T12:52:27.453Z",
            date3: "2019-12-06T12:54:27.453Z",
            renderCount: 0,
            pageSizes: ["10", "25", "50", "100"],
            statuses: [
                {
                    status: "Online",
                    langKey: "message.online",
                    class: "online",
                    icon: "check-circle",
                    order: 1,
                },
                {
                    status: "Away",
                    langKey: "message.beRightBack",
                    class: "away",
                    icon: "clock",
                    order: 2,
                },
                {
                    status: "Busy",
                    langKey: "message.busy",
                    class: "busy",
                    icon: "minus-circle",
                    order: 3,
                },
                {
                    status: "Offline",
                    langKey: "message.offline",
                    class: "offline",
                    icon: "power-off",
                    order: 4,
                },
            ],
            fields: [
                {
                    key: "isOnline",
                    label: this.$t("message.status"),
                },
                {
                    key: "name",
                    label: this.$t("message.repName"),
                },
                {
                    key: "lastActivity",
                    label: this.$t("message.lastOnline"),
                    sortable: true,
                },
                {
                    key: "lastLoginPlatform",
                    label: this.$t("message.lastLoginPlatform"),
                    sortable: true,
                },
                {
                    key: "lastUserAction",
                    label: this.$t("message.lastStatusChange"),
                    sortable: true,
                },
                {
                    key: "conversationCount",
                    label: this.$t("message.activeChat"),
                    sortable: true,
                },
                {
                    key: "actions",
                    label: this.$t("message.activeChat"),
                    sortable: true,
                },
            ],
            paginationCursor: {
                page: 1,
                perPage: 10,
                total: 0,
            },
            page: 1,
            currentSort: {
                columnKey: "isOnline",
                order: "ascend",
            },
        };
    },
    computed: {
        ...mapState({
            activeConversation: (state) => state.chat.activeContact?.lastConversation,
            activeContact: (state) => state.chat.activeContact,
            users: (state) => state.user.users,
            site: (state) => state.site.site,
            settings: (state) => state.setting.settings,
            loggedUser: (state) => state.auth.user || state.auth.adminUser,
            isRoot: (state) => !!state.auth.adminUser || _.get(state.auth.user, "role.isRoot"),
            user: (state) => state.auth.user,
            isAdmin: (state) => !!state.auth.adminUser,
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),
        totalUserLength() {
            if (this.showOnlineAgents) {
                return this.users.data.filter((x) => x.isOnline).length;
            } else {
                return this.users.data.length;
            }
        },
    },

    methods: {
        ...mapActions({
            getUsers: "user/getUsers",
            getUser: "user/getUser",
            leaveContactConversation: "chat/leaveContactConversation",
            hideConversation: "chat/hideConversation",
        }),
        startDM(id) {
            this.$router.push("/chat?tabIndex=teamChat");
            setTimeout(() => {
                SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.START_AGENT_CHAT, { userId: id });
            }, 500);
        },

        goToReportsWithToday(id) {
            this.$router.push({
                path: "/report-new",
                query: { selectedUsers: id },
            });
        },
        getUserDetailedLoginInfo(user) {
            this.infoButtonVisible = true;
            const parser = require("ua-parser-js");
            const ua = parser(user.lastLoginUserAgent || "-");
            const defaultGeo = {
                country: "",
                city: "",
            };
            return `<div class="d-flex flex-column justify-content-start align-items-start w-100">
                      <b>${this.$t("message.ipAddress")} ${user.lastLoginIPAddress}</b>
                      <b>${this.$t("message.country")}:${(user.lastGeoLocation || defaultGeo).country} - ${
                (user.lastGeoLocation || defaultGeo).city
            }</b>
                      <b>${this.$t("message.os")}:${ua.os.name} ${ua.os.version}</b>
                      <b>${this.$t("message.browser")}: ${ua.browser.name} ${ua.browser.major}</b>
                 </div>`;
        },

        getUserMaxChatCapacity(id) {
            const maybeUser = this.users.data.find((x) => x._id === id);
            let result = 0;
            if (maybeUser) {
                const userLimit = maybeUser.autoAssignLimit;
                const systemLimit = Math.max(this.settings.autoAssignDefaultLimit, 0);
                if (userLimit < 0) {
                    result = systemLimit;
                } else {
                    result = userLimit;
                }
            }
            return result;
        },
        getCols() {
            return [
                {
                    title: this.$t("message.status"),
                    field: "isOnline",
                    key: "isOnline",
                    width: "80px",
                    renderBodyCell: ({ row }) => {
                        const isForceOnline = row.lastLoginPlatform === "mobile" && row.status === "Online";
                        const status = this.statuses.find((s) => s.status === (isForceOnline ? "Online" : row.status));
                        log("isForceOnline " + row.name, {
                            isForceOnline,
                            status,
                            row,
                        });
                        return row.isOnline || isForceOnline ? (
                            <span class={status.class}>
                                <font-awesome-icon icon={status.icon} class="mr-2" />
                                {this.$t(status.langKey)}
                            </span>
                        ) : (
                            <span class="offline">
                                <font-awesome-icon class="mr-2" icon="power-off" />
                                {this.$t("message.offline")}
                            </span>
                        );
                    },
                },
                {
                    title: this.$t("message.repName"),
                    field: "name",
                    key: "name",
                    width: "140px",
                },
                {
                    title: this.$t("message.lastOnline"),
                    field: "lastActivity",
                    key: "lastActivity",
                    width: "140px",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div>
                                {row.isOnline ? (
                                    <PassedTimeCounterTimeFormat
                                        id={`${row._id}-${this.$i18n.locale}`}
                                        key={`${row._id}-${this.$i18n.locale}`}
                                        date={row.lastActivity}
                                    />
                                ) : (
                                    <span>{this.$options.filters.dateShorted(row.lastActivity)}</span>
                                )}
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("message.lastLoginPlatform"),
                    field: "lastLoginPlatform",
                    key: "lastPlatform",
                    width: "140px",

                    renderBodyCell: ({ row }) => {
                        return row.lastLoginPlatform === "mobile" ? (
                            <div class="mr-2 text-primary">
                                <a-icon type="mobile" />
                                Mobile
                            </div>
                        ) : (
                            <div>
                                <a-icon class="mr-2 text-primary" type="chrome" />
                                WEB
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("message.lastStatusChange"),
                    field: "lastUserAction",
                    key: "lastUserAction",

                    width: "140px",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div>
                                {row.lastStatusChangeDate ? (
                                    <div>
                                        {row.status === "Online" ? (
                                            <a-badge color="#1BC5BD" />
                                        ) : row.status === "Away" ? (
                                            <a-badge color="#3699FF" />
                                        ) : row.status === "Busy" ? (
                                            <a-badge color="#FFA829" />
                                        ) : row.status === "Offline" ? (
                                            <a-badge color="#F64E60"></a-badge>
                                        ) : (
                                            ""
                                        )}
                                        <PassedTimeCounterTimeFormat
                                            id={`${row._id}-${this.$i18n.locale}`}
                                            key={`${row._id}-${this.$i18n.locale}`}
                                            date={new Date(row.lastStatusChangeDate).toISOString()}
                                            second-visible={false}
                                            shortFormat={true}
                                            suffix={this.$t("message.ago")}
                                        />
                                    </div>
                                ) : (
                                    <span>-</span>
                                )}
                            </div>
                        );
                    },
                },

                {
                    title: this.$t("message.activeChat"),
                    field: "conversationCount",
                    key: "conversationCount",
                    width: "140px",
                    renderBodyCell: ({ row }) => {
                        return row.status === "Offline" ? <span>0 -</span> : <span>{row.conversationCount || 0}</span>;
                    },
                },
                {
                    title: this.$t("message.userMaxCapacity"),
                    field: "userMaxCapacity",
                    key: "userMaxCapacity",
                    width: "140px",
                    renderBodyCell: ({ row }) => {
                        return this.getUserMaxChatCapacity(row._id);
                    },
                },
                {
                    title: this.$t("message.options"),
                    field: "actionSlot",
                    key: "actionSlot",
                    width: "140px",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="d-flex justify-content-start align-items-center">
                                {this.assign && (
                                    <b-button size="sm" on-click={() => this.assignAgent(row._id)}>
                                        {this.$t("message.addChat")}
                                    </b-button>
                                )}
                                {this.forward && (
                                    <b-button size="sm" on-click={() => this.forwardAgent(row._id)}>
                                        {this.$t("message.forwardChat")}
                                    </b-button>
                                )}
                                {this.isRoot && this.infoButtonVisible && (
                                    <a-icon
                                        v-tippy={{
                                            followCursor: true,
                                            placement: "bottom-end",
                                            theme: this.isDarkMode ? "light-border" : "google",
                                        }}
                                        content={this.getUserDetailedLoginInfo(row)}
                                        class="mr-2"
                                        style="font-size: 20px; color: #1bc5bd; background-color: #ffffff; border-radius: 50%"
                                        theme="filled"
                                        type="info-circle"
                                    />
                                )}
                                <a-tooltip>
                                    <template slot="title">{this.$t("report.reports")}</template>
                                    {this.reportButton && (
                                        <a-icon
                                            style="border: 1px solid #1bc5bd; padding: 4px; box-sizing: border-box; font-size: 12px; color: #1bc5bd; background-color: #ffffff; border-radius: 50%;"
                                            type="line-chart"
                                            on-click={() => this.goToReportsWithToday(row._id)}
                                        />
                                    )}
                                </a-tooltip>
                                <a-tooltip on-click={() => this.startDM(row._id)}>
                                    <template slot="title">{this.$t("message.message")}</template>
                                    {this.loggedUser._id !== row._id && <dm-icon id="dmIcon" type="line-chart" />}
                                </a-tooltip>
                            </div>
                        );
                    },
                },
            ];
        },
        items() {
            if (this.assign || this.forward) {
                if (this.defaultContacts && this.defaultContacts.length > 0) {
                    return this.users.data.length > 0
                        ? this.users.data
                              .filter(
                                  (u) =>
                                      (u.isOnline || u.lastLoginPlatform === "mobile") &&
                                      !this.defaultContacts.some(
                                          (contact) =>
                                              contact.lastConversation && contact.lastConversation.users.includes(u._id)
                                      )
                              )
                              .filter((u) => u._id !== this.loggedUser._id)
                              .slice(
                                  this.paginationCursor.perPage * (this.paginationCursor.page - 1),
                                  this.paginationCursor.perPage * this.paginationCursor.page
                              )
                        : [];
                } else {
                    return this.activeConversation != null && this.users.data.length > 0
                        ? this.users.data
                              .filter(
                                  (u) =>
                                      (u.isOnline || u.lastLoginPlatform === "mobile") &&
                                      !this.activeConversation.users.includes(u._id)
                              )
                              .filter((u) => u._id !== this.loggedUser._id)
                              .slice(
                                  this.paginationCursor.perPage * (this.paginationCursor.page - 1),
                                  this.paginationCursor.perPage * this.paginationCursor.page
                              )
                        : [];
                }
            } else {
                let items = [...this.users.data];
                items = items.map((x) => {
                    if (!this.isAdmin && x._id === this.user._id) {
                        x.status = this.user.status;
                        x.isOnline = true;
                    }
                    x.statusOrder = this.statuses.filter((w) => w.status === x.status)[0].order;
                    x.lastActivityDate = new Date(x.lastActivity).setSeconds(0.0); // user_update ler tek tek geliyor sıra değişiyordu sürekli o yüzden saniye kısmını ignore edince 60 sn içindeki değişiklik aynı sırada kabul edilecek
                    return x;
                });
                items = _.orderBy(
                    items,
                    ["isOnline", "statusOrder", "lastActivityDate", "repName"],
                    ["desc", "asc", "desc", "asc"]
                );
                let resultItems = items;
                if (this.showOnlineAgents) {
                    resultItems = resultItems.filter((x) => x.isOnline);
                }

                return resultItems.slice(
                    this.paginationCursor.perPage * (this.paginationCursor.page - 1),
                    this.paginationCursor.perPage * this.paginationCursor.page
                );
            }
        },
        assignAgent(id) {
            log("assign agent", id);
            if (this.defaultContacts && this.defaultContacts.length > 0) {
                this.defaultContacts.forEach((contact) => {
                    if (contact.lastConversation) {
                        sendSocketMessage("user add-user-in-conversation", {
                            conversationId: contact.lastConversation._id,
                            userId: id,
                            siteId: this.site._id,
                            type: "assign",
                        });
                    }
                });
            } else if (this.activeConversation) {
                sendSocketMessage("user add-user-in-conversation", {
                    conversationId: this.activeConversation._id,
                    userId: id,
                    siteId: this.site._id,
                    type: "assign",
                });
            }
            this.$emit("close");
            this.$bvModal.hide("assign-agent");
        },
        forwardAgent(userId) {
            if (this.defaultContacts && this.defaultContacts.length > 0) {
                this.defaultContacts.forEach((contact) => {
                    if (contact.lastConversation) {
                        const conversationId = contact.lastConversation._id;
                        log("forwarding agent", userId, {
                            conversation: conversationId,
                            contact: contact,
                        });

                        this.leaveContactConversation(contact);
                        setTimeout(() => {
                            sendSocketMessage("user add-user-in-conversation", {
                                conversationId,
                                userId,
                                siteId: this.site._id,
                                forward: {
                                    to: userId,
                                },
                                type: "forward",
                                forwarderUserId: this.user?._id,
                            });
                        }, 500);
                    }
                });
            } else if (this.activeConversation) {
                const conversationId = this.activeConversation._id;
                log("forwarding agent", userId, {
                    activeConversation: conversationId,
                    activeContact: this.activeContact,
                });

                if (!conversationId) return;
                this.leaveContactConversation(this.activeContact);
                setTimeout(() => {
                    sendSocketMessage("user add-user-in-conversation", {
                        conversationId,
                        userId,
                        siteId: this.site._id,
                        forward: {
                            to: userId,
                        },
                        type: "forward",
                        forwarderUserId: this.user?._id,
                    });
                }, 500);
            }

            this.$emit("close");
            this.$bvModal.hide("forward-agent");
        },
        get() {
            this.getUsers({
                perPage: 1000,
                page: 1,
            }).then(({ data }) => {
                this.paginationCursor.total = data.total;
            });
        },
    },
    mounted() {
        this.get();
        this.sub = systemMessageStream.pipe(filter((x) => x.type === "agent-updated")).subscribe((x) => {
            log("agents changed");
            this.$forceUpdate();
        });
    },
    destroyed() {
        if (this.sub) this.sub.unsubscribe();
    },
    updated() {
        if (moment.locale() !== this.$i18n.locale) moment.locale(this.$i18n.locale);
    },
    watch: {
        "paginationCursor.perPage"() {
            this.paginationCursor.page = 1;
        },
        users(newValue, old) {
            log("users changed");
            if (!_.isEqual(old, newValue)) {
                log("$forceUpdate running");
                this.$forceUpdate();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
#agent-list {
    padding: 10px;

    .online {
        .svg-inline--fa {
            color: #1bc5bd !important;
        }
    }

    .offline {
        .svg-inline--fa {
            color: #f64e60 !important;
        }
    }

    .away {
        .svg-inline--fa {
            color: #3699ff !important;
        }
    }

    .busy {
        .svg-inline--fa {
            color: #ffa829 !important;
        }
    }

    #dmIcon {
        margin-left: 7px;
        cursor: pointer;
        width: 100px !important;
    }

    .filterContainer {
        display: flex;
        justify-content: left;
        width: 40%;
        position: absolute;
        margin-top: 2px;
        z-index: 50;

        div {
            margin-right: 3px;
        }
    }

    .empty {
        position: relative !important;
        margin-top: 0px !important;
        margin-bottom: 5px;
    }
}
</style>
